<template>
    <!-- Versión compacta para barra de estado -->
    <div v-if="compact" class="autosave-compact">
        <!-- Indicador de estado compacto -->
        <transition name="fade">
            <div v-if="lastSaveTime" class="compact-save-indicator">
                <i class="fas fa-cloud-check"></i>
                <span>{{ formattedSaveTime }}</span>
            </div>
        </transition>

        <!-- Cambios sin guardar compacto -->
        <transition name="fade">
            <div v-if="hasUnsavedChanges && !lastSaveTime" class="compact-unsaved">
                <div class="compact-pulse-dot"></div>
                <span>Sin guardar</span>
            </div>
        </transition>

        <!-- Notificación de borrador compacta (solo si NO es inline) -->
        <transition name="slide-down">
            <div v-if="showDraftNotification && !inline" class="compact-draft-notification">
                <div class="compact-draft-content">
                    <i class="fas fa-file-alt"></i>
                    <span>Borrador disponible</span>
                    <div class="compact-draft-actions">
                        <button class="compact-btn-restore" title="Restaurar borrador" @click="restoreDraft">
                            <i class="fas fa-undo"></i>
                        </button>
                        <button class="compact-btn-dismiss" title="Descartar borrador" @click="dismissDraft">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>

    <!-- Versión completa original -->
    <div v-else class="autosave-container">
        <!-- Indicador de estado -->
        <transition name="fade">
            <div v-if="lastSaveTime" class="autosave-indicator">
                <div class="indicator-content">
                    <i class="fas fa-cloud-upload-alt icon-cloud"></i>
                    <span class="save-text">
                        Guardado {{ formattedSaveTime }}
                    </span>
                </div>
            </div>
        </transition>

        <!-- Notificación de borrador disponible -->
        <transition name="slide-down">
            <div v-if="showDraftNotification" class="draft-notification">
                <div class="draft-content">
                    <i class="fas fa-file-alt draft-icon"></i>
                    <div class="draft-text">
                        <strong>Borrador disponible</strong>
                        <p>Se encontró un borrador guardado automáticamente</p>
                    </div>
                    <div class="draft-actions">
                        <button class="btn-restore" @click="restoreDraft">
                            <i class="fas fa-undo"></i>
                            Restaurar
                        </button>
                        <button class="btn-dismiss" @click="dismissDraft">
                            <i class="fas fa-times"></i>
                            Descartar
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Indicador de cambios sin guardar -->
        <transition name="pulse">
            <div v-if="hasUnsavedChanges && !lastSaveTime" class="unsaved-indicator">
                <div class="pulse-dot"></div>
                <span>Cambios sin guardar</span>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
    lastSaveTime: {
        type: Date,
        default: null
    },
    hasUnsavedChanges: {
        type: Boolean,
        default: false
    },
    hasDraft: {
        type: Boolean,
        default: false
    },
    formattedSaveTime: {
        type: String,
        default: ''
    },
    compact: {
        type: Boolean,
        default: false
    },
    inline: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['restore-draft', 'discard-draft']);

const showDraftNotification = ref(false);

const restoreDraft = () => {
    emit('restore-draft');
    showDraftNotification.value = false;
};

const dismissDraft = () => {
    emit('discard-draft');
    showDraftNotification.value = false;
};

// Watch para detectar cuando hasDraft cambia a true
watch(
    () => props.hasDraft,
    (newValue) => {
        if (newValue) {
            showDraftNotification.value = true;
        }
    },
    { immediate: true } // Ejecutar inmediatamente al montar
);

onMounted(() => {
    if (props.hasDraft) {
        showDraftNotification.value = true;
    }
});
</script>

<style scoped>
.autosave-container {
    position: relative;
}

.autosave-indicator {
    position: fixed;
    top: 80px;
    right: 30px;
    z-index: 1000;
}

.indicator-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #68d391 0%, #48bb78 100%);
    color: white;
    padding: 0.65rem 1.1rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(104, 211, 145, 0.3);
}

.icon-cloud {
    font-size: 1rem;
    animation: cloudFloat 2s ease-in-out infinite;
}

@keyframes cloudFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
}

.save-text {
    font-size: 0.85rem;
}

.draft-notification {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10001;
    max-width: 500px;
    width: 90%;
}

.draft-content {
    background: white;
    border-radius: 16px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 1rem;
    border-left: 4px solid #667eea;
}

.draft-icon {
    font-size: 2rem;
    color: #667eea;
    flex-shrink: 0;
}

.draft-text {
    flex: 1;
}

.draft-text strong {
    color: #2d3748;
    font-size: 1rem;
    display: block;
    margin-bottom: 0.25rem;
}

.draft-text p {
    margin: 0;
    color: #718096;
    font-size: 0.85rem;
}

.draft-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.btn-restore,
.btn-dismiss {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-restore {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.btn-restore:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-dismiss {
    background: #f7fafc;
    color: #718096;
}

.btn-dismiss:hover {
    background: #edf2f7;
    color: #4a5568;
}

.unsaved-indicator {
    position: fixed;
    top: 80px;
    right: 30px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #fc8181 0%, #f56565 100%);
    color: white;
    padding: 0.65rem 1.1rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(252, 129, 129, 0.3);
    z-index: 1000;
}

.pulse-dot {
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.5;
        transform: scale(1.2);
    }
}

/* Estilos para versión compacta */
.autosave-compact {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    position: relative;
}

.compact-save-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.85rem;
    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
    border: 1px solid #9ae6b4;
    border-radius: 20px;
    color: #2f855a;
    font-size: 0.8rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(72, 187, 120, 0.1);
}

.compact-save-indicator i {
    color: #48bb78;
    font-size: 0.95rem;
}

.compact-unsaved {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.85rem;
    background: linear-gradient(135deg, #fffaf0 0%, #feebc8 100%);
    border: 1px solid #fbd38d;
    border-radius: 20px;
    color: #7c2d12;
    font-size: 0.8rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(237, 137, 54, 0.1);
}

.compact-pulse-dot {
    width: 8px;
    height: 8px;
    background: #ed8936;
    border-radius: 50%;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.compact-draft-notification {
    position: fixed;
    top: 70px;
    right: 20px;
    background: linear-gradient(135deg, #ebf4ff 0%, #e9d8fd 100%);
    border: 2px solid #805ad5;
    border-radius: 12px;
    padding: 0.75rem 1rem;
    box-shadow: 0 10px 25px rgba(128, 90, 213, 0.2);
    z-index: 9999;
    animation: slideInFromRight 0.4s ease;
}

.compact-draft-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.compact-draft-content i {
    color: #805ad5;
    font-size: 1.2rem;
}

.compact-draft-content span {
    color: #553c9a;
    font-weight: 600;
    font-size: 0.9rem;
}

.compact-draft-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
}

.compact-btn-restore,
.compact-btn-dismiss {
    padding: 0.4rem 0.6rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.compact-btn-restore {
    background: linear-gradient(135deg, #805ad5 0%, #6b46c1 100%);
    color: white;
}

.compact-btn-restore:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(128, 90, 213, 0.3);
}

.compact-btn-dismiss {
    background: rgba(160, 174, 192, 0.2);
    color: #4a5568;
}

.compact-btn-dismiss:hover {
    background: rgba(160, 174, 192, 0.3);
}

@keyframes slideInFromRight {
    from {
        opacity: 0;
        transform: translateX(100px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* Animaciones */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.slide-down-enter-active {
    animation: slideDown 0.4s ease;
}

.slide-down-leave-active {
    animation: slideUp 0.3s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translate(-50%, -20px);
    }
    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}

@keyframes slideUp {
    from {
        opacity: 1;
        transform: translate(-50%, 0);
    }
    to {
        opacity: 0;
        transform: translate(-50%, -20px);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .autosave-indicator,
    .unsaved-indicator {
        top: 70px;
        right: 20px;
        font-size: 0.75rem;
        padding: 0.5rem 0.85rem;
    }
    
    .draft-notification {
        top: 10px;
        width: 95%;
    }
    
    .draft-content {
        flex-direction: column;
        text-align: center;
    }
    
    .draft-actions {
        width: 100%;
        flex-direction: column;
    }
    
    .btn-restore,
    .btn-dismiss {
        width: 100%;
        justify-content: center;
    }
}
</style>
